import { ColumnDef } from '@tanstack/react-table'
import { useCallback, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { PiMagnifyingGlass } from 'react-icons/pi'
import { toast } from 'react-toastify'
import {
  ActionsDropdown,
  createEditAction,
  createDeleteAction,
} from '../../../components/Buttons/ActionsDropdown'
import confirm from '../../../components/dialogConfirm'
import {
  ClientDataTable,
  useIsRowHovered,
} from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { FilterChipsComponent } from '../../../components/Filters/components/FilterChips/FilterChips'
import { EditSavedFilterModal } from '../../../components/Filters/saved-filters/EditSavedFilterModal'
import { DateCell } from '../../../components/Tables/CommonTableCells/DateCell'
import {
  FilterIdentifier,
  SavedView,
  SavedViewTableLabels,
} from '../../../models/saved_view'
import useApiService from '../../../services/api'

function HoverableFilterChips({
  payload,
  identifier,
  rowId,
}: {
  payload: SavedView['payload']
  identifier: FilterIdentifier
  rowId: string
}) {
  const isHovered = useIsRowHovered(rowId)

  return (
    <div className="py-2">
      <FilterChipsComponent
        filterState={{
          valueFilters: payload.valueFilters,
          rangeFilters: payload.rangeFilters,
          zipCodeFilter: payload.zipCodeFilter,
          criteria: payload.criteria,
          search: payload.search,
        }}
        identifier={identifier}
        isHovered={isHovered}
      />
    </div>
  )
}

export function SavedFiltersTable() {
  const api = useApiService()

  const { data: categories } = api.useGetActiveCompanyCategoriesAndTypes()
  const {
    data: rawData,
    isLoading,
    refetch,
    isFetching,
  } = api.useGetAllSavedFilters()

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<SavedView | null>(null)

  // Client-side search: all data is loaded at once (no pagination), so filtering in-memory is sufficient.
  const data = useMemo(() => {
    if (!searchQuery || !rawData) return rawData
    const q = searchQuery.toLowerCase()
    return rawData.filter((item) => item.name?.toLowerCase().includes(q))
  }, [rawData, searchQuery])

  const handleDelete = useCallback(
    async (filterId: number) => {
      const confirmed = await confirm(
        'Are you sure you want to delete this saved filter?',
        'Delete Saved Filter',
        'This action cannot be undone.',
        { confirmLabel: 'Delete', buttonVariant: 'danger' }
      )

      if (!confirmed) {
        return
      }

      try {
        await api.deleteFilter(filterId)
        toast.success('Saved filter deleted successfully')
        refetch()
      } catch (err) {
        console.error('Failed to delete saved filter:', err)
        toast.error('Failed to delete saved filter')
      }
    },
    [api, refetch]
  )

  const handleEdit = useCallback((filter: SavedView) => {
    setSelectedFilter(filter)
    setEditModalOpen(true)
  }, [])

  const columns = useMemo<ColumnDef<SavedView, any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 220,
        meta: { isFirstColumn: true },
      },
      {
        accessorKey: 'filters',
        header: 'Filters',
        size: 500,
        cell: (info) => (
          <HoverableFilterChips
            payload={info.row.original.payload}
            identifier={info.row.original.identifier}
            rowId={info.row.id}
          />
        ),
      },
      {
        accessorKey: 'identifier',
        header: 'Table',
        size: 180,
        cell: (info) => {
          if (info.getValue().toString().startsWith('companies-')) {
            const id = parseInt(info.getValue().toString().split('-')[1])
            let name = ''
            if (categories) {
              loop: for (const c of categories) {
                for (const s of c.sub_categories) {
                  if (s.id === id) {
                    name = s.name
                    break loop
                  }
                }
              }
            }

            return name
          } else {
            return SavedViewTableLabels[info.getValue() as FilterIdentifier]
          }
        },
      },
      {
        accessorKey: 'shared',
        size: 100,
        header: 'Shared',
        cell: (info) => (info.getValue() ? 'Yes' : 'No'),
      },
      {
        id: 'user__first_name',
        header: 'Created By',
        size: 200,
        cell: (info) => {
          const user = info.row.original?.user
          const initials =
            `${user?.first_name?.[0] ?? ''}${user?.last_name?.[0] ?? ''}`.toUpperCase()
          const fullName =
            `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()
          return (
            <div className="flex items-center gap-2.5">
              <div
                className="flex shrink-0 items-center justify-center rounded-full text-xs font-medium leading-4"
                style={{
                  width: 24,
                  height: 24,
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  letterSpacing: '-0.2px',
                }}
              >
                {initials}
              </div>
              <span className="truncate">{fullName}</span>
            </div>
          )
        },
      },
      {
        accessorKey: 'created',
        header: 'Date Created',
        size: 160,
        cell: DateCell,
      },
      {
        accessorKey: 'modified',
        header: 'Date Modified',
        size: 160,
        cell: DateCell,
      },
      {
        id: 'actions',
        header: () => <div className="w-full text-center">Actions</div>,
        size: 70,
        meta: {
          isLastColumn: true,
          headerDisableOrdering: true,
        },
        cell: ({ row }) => (
          <ActionsDropdown
            className="!w-full justify-center"
            actions={[
              createEditAction(() => handleEdit(row.original)),
              createDeleteAction(() => handleDelete(row.original.id)),
            ]}
          />
        ),
      },
    ],
    [categories, handleDelete, handleEdit]
  )

  if (isLoading || !rawData || isFetching) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <TableLinkStyleProvider variant="plain">
      <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <DataTableContainer className="h-full">
          <ClientDataTable
            tableHeader={
              <div
                data-table-toolbar
                className="flex flex-row items-center gap-3 px-4 py-3"
              >
                <div className="relative w-full max-w-md">
                  <PiMagnifyingGlass
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search saved filters..."
                    className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
                  />
                </div>
              </div>
            }
            height="100%"
            stickyFirstColumn={true}
            stickyLastColumn={true}
            enableRowHover={true}
            enableRightCellAlignment={true}
            data={data || []}
            columns={columns}
          />
        </DataTableContainer>
      </div>
      <EditSavedFilterModal
        show={editModalOpen}
        handleClose={() => {
          setEditModalOpen(false)
          setSelectedFilter(null)
        }}
        savedFilter={selectedFilter}
        onFilterUpdated={refetch}
      />
    </TableLinkStyleProvider>
  )
}
